import { createContext, useContext, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Topbar (AppShell) — หน้าไหนก็ส่ง "ชื่อหน้า" และ "ปุ่มหลักของหน้า" ขึ้นไปแสดงบนแถบบนได้
 * (แบบเดียวกับ kkautoweb: ชื่อหน้าอยู่บน topbar ไม่ซ้ำในเนื้อหา)
 *
 * - ชื่อหน้า: ค่าเริ่มต้นมาจากเมนู (NAV_SECTIONS) · <PageHeader title> เรียก useTopbarTitle ให้เอง
 * - ปุ่มหลัก: <TopbarActions>…</TopbarActions> หรือ <PageHeader primaryAction={…}>
 */
type TopbarContextValue = {
  /** จุดวางปุ่มบน topbar (null ระหว่าง mount ครั้งแรก) */
  actionsSlot: HTMLElement | null;
  setTitle: (title: string | null) => void;
};

export const TopbarContext = createContext<TopbarContextValue | null>(null);

/** ตั้งชื่อหน้าบน topbar ระหว่างที่ component นี้ mount อยู่ — unmount แล้วกลับไปใช้ชื่อจากเมนู */
export function useTopbarTitle(title: string | undefined) {
  const ctx = useContext(TopbarContext);
  const setTitle = ctx?.setTitle;
  useEffect(() => {
    if (!setTitle || !title) return;
    setTitle(title);
    return () => setTitle(null);
  }, [setTitle, title]);
}

/**
 * true = component อยู่ในช่องปุ่มของ topbar · <Button> ใช้ค่านี้ห่อข้อความด้วย [data-label]
 * แล้วช่องปุ่มซ่อนข้อความใต้ sm (เหลือไอคอน) + ตั้ง aria-label/title จากข้อความให้เอง
 */
const TopbarSlotContext = createContext(false);
export function useInTopbarSlot() {
  return useContext(TopbarSlotContext);
}

/**
 * วาง children ลงช่องปุ่มของ topbar (ขวามือ ก่อนชิปผู้ใช้) — นอก AppShell จะไม่แสดงอะไร
 * จอแคบกว่า sm ปุ่มในช่องนี้เหลือแค่ไอคอน (ชื่อหน้าต้องอ่านออก) → ปุ่มต้องมีไอคอน lucide เสมอ
 */
export function TopbarActions({ children }: { children: ReactNode }) {
  const slot = useContext(TopbarContext)?.actionsSlot;
  return slot
    ? createPortal(<TopbarSlotContext.Provider value={true}>{children}</TopbarSlotContext.Provider>, slot)
    : null;
}
